import React from "react";
import ReactMarkdown, { Components } from "react-markdown";
import { PrismLight as SyntaxHighlighter } from "./prismLight";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { linkifyText } from "./linkify";
import { Element } from "hast";
import { ClassAttributes, HTMLAttributes, HTMLProps } from "react";
import { ChatMessageStatus, ChatCitationSource } from "../FpPages/Chat/types";
import {
  ChatCalculationSource,
  ChatIodlSource,
  chatSourceCalculation,
  chatSourceIodl,
  chatSourceKey,
  chatSourceKind,
  chatSourceLabel,
} from "../FpPages/Chat/evidenceFooter";
import {
  CitationPassagesContext,
  needsPassage,
  openChatCitationWithPassage,
} from "./citationPassages";
import { parseCitationDeepLink } from "../../services/citationDeepLink";
import { getSourceMapForQid } from "../ReferenceWrapper/services/getSourceRefs";
import { fetchWorkspace } from "../FpEditor/services/useWorkspaceData";
import { CITATION_REGEX, SIMPLE_CITATION_REGEX } from "../../constants";
import { MultipleCitationsButton } from "./MultipleCitationsButton";
import { File } from "lucide-react";
import RecordMentionChip from "../FpPages/Chat/components/RecordMentionChip";
import CalculationCard from "../FpPages/Chat/components/CalculationCard";
import type { EntityKind } from "../FpPages/RMS/Dashboard/types";
import { extractTrailingIdFromSlug } from "../FpPages/RMS/Dashboard/utils/slugUtils";

interface MarkdownRendererProps {
  content: string;
  queryId?: string;
  onCitationClick?: (citationId: string) => void;
  status?: ChatMessageStatus;
  isUserMessage?: boolean;
  // Legacy-shaped citations for the agentic-chat path, keyed by the bracketed
  // `[NNNNN.M]` marker. When present, those markers in the content resolve into
  // clickable figures/chips against this map; markers with no entry render as
  // plain text with the marker dropped (legacy-chat citations).
  chatSourceMap?: Record<string, ChatCitationSource[]>;
  // The number each marker's chip carries, when the answer's sources list
  // already numbered them (#11426): one derivation numbers the chips and the
  // list, so a chip and its row in the list agree, across every text block of
  // a turn. Without it, chips number themselves per source in the order this
  // block cites them.
  citationNumbers?: Record<string, number>;
  // Force-show every citation chip even when it follows a citable
  // numeric. Default behavior (false) hides such chips because the
  // preceding number itself becomes the click target via post-
  // processing. Source-modal-style contexts where the entire body is
  // often just "<value> [N.N]" need the chip visible — there's no
  // surrounding prose to anchor the click on otherwise.
  forceShowCitations?: boolean;
  // Set by a page that mounts its own `<FileViewer />` (named agents renders
  // one in a split pane). Document citations then ask Layout to populate the
  // viewer state without also mounting the global viewer, so the click opens
  // one pane rather than two. Chat leaves this unset — it has no inline viewer
  // and relies on Layout's entirely.
  inlineViewerOnly?: boolean;
}

interface CodeProps
  extends ClassAttributes<HTMLElement>,
    HTMLAttributes<HTMLElement> {
  node?: Element;
  children?: React.ReactNode;
}

interface ElementProps extends HTMLProps<HTMLElement> {
  node?: Element;
  children?: React.ReactNode;
}

interface CitationButtonElementProps {
  citationId: string;
  queryId: string;
  displayNumber: number;
  node?: Element;
}

interface MultipleCitationsElementProps {
  citations: string; // JSON string of array of {citationId, displayNumber}
  queryId: string;
  node?: Element;
}

interface ChatCitationElementProps {
  displayLabel: string;
  displayNumber: number;
  // The bracketed marker the chip stands for, so a chip whose entry carries
  // no passage can ask for it (#12081).
  markerKey?: string;
  // When non-empty, the cited figure to underline inline (e.g. "32.7%")
  // instead of rendering a trailing number chip.
  displayText?: string;
  // Exactly one open-target discriminator is set per node:
  // - docId: open the cited document (existing behavior)
  // - publicId + kind: open the RMS entity preview, on `factName` when the
  //   citation names one attribute of the record (#11369)
  // - interactionId: open the RMS interaction viewer
  // - webUrl: open the URL in a new tab
  // - iodlPayload: open the IODL source viewer (JSON: sql/columns/rows/...)
  docId?: string;
  publicId?: string;
  kind?: string;
  factName?: string;
  interactionId?: string;
  artifactId?: string;
  webUrl?: string;
  iodlPayload?: string;
  // - calculationPayload: open the calculation card (JSON, #13618)
  calculationPayload?: string;
  // Evidence anchor for document citations: the harvested page number and
  // verbatim chunk text, so the viewer opens AT the cited passage (#10310).
  // pageNum arrives as a string — hProperties stringify through the HTML
  // attribute layer — and is coerced back to a number before dispatch.
  pageNum?: number | string;
  sourceContent?: string;
  node?: Element;
}

interface CellCoordinates {
  letterCoord: string;
  numberCoord: number;
}

/**
 * Parses cell coordinates from content that starts with "Cell:"
 * Format: "Cell: {row_num}{col_letter} and {col_letter}{row_num}"
 * Example: "Cell: 1B and B1\n" -> { letterCoord: "B", numberCoord: 1 }
 * Handles multi-digit numbers and multi-letter column names (AA, AB, etc.)
 */
const parseCellCoordinates = (content: string): CellCoordinates | null => {
  if (!content.startsWith("Cell:")) {
    return null;
  }

  // Extract the part after "Cell:" and before any newlines
  const cellPart = content.substring(5).split("\n")[0].trim();

  // Look for the pattern: {number}{letter} and {letter}{number}
  // This handles multi-digit numbers and multi-letter column names
  const cellRegex = /(\d+)([A-Z]+)\s+and\s+([A-Z]+)(\d+)/;
  const match = cellPart.match(cellRegex);

  if (!match) {
    return null;
  }

  // match[1] = first number, match[2] = first letter, match[3] = second letter, match[4] = second number
  // We want the first format (number + letter) as our coordinates
  return {
    letterCoord: match[2], // The letter from the first part
    numberCoord: parseInt(match[1], 10), // The number from the first part
  };
};

/**
 * Cleans up malformed markdown links that have nested link syntax.
 * Fixes patterns like [$NUMBER](URL1)](URL2)] to just [$NUMBER](URL1)
 *
 * @param markdown - The markdown content to clean
 * @returns The cleaned markdown content
 */
const cleanMalformedMarkdownLinks = (markdown: string): string => {
  // Pattern to match: [text](url1)](url2)] - where the first link is valid
  // but there's a trailing malformed ](url2)] part
  // This regex looks for:
  // - [text](url) - a valid markdown link
  // - followed by ](url)] - malformed nested link syntax
  // The pattern allows for optional whitespace between the links
  const malformedLinkPattern = /\[([^\]]+)\]\(([^)]+)\)\]\s*\(([^)]+)\)\]/g;

  return markdown.replace(
    malformedLinkPattern,
    (match, linkText, firstUrl, secondUrl) => {
      // Return just the first valid link, discarding the malformed trailing part
      return `[${linkText}](${firstUrl})`;
    }
  );
};

/**
 * Recursively processes React children to convert <br> text nodes into actual <br /> elements.
 * ReactMarkdown escapes HTML, so <br> tags appear as plain text. This function converts them.
 *
 * @param children - The React children to process
 * @returns Processed children with <br> tags converted to line breaks
 */
const processBrTagsInChildren = (
  children: React.ReactNode
): React.ReactNode => {
  if (typeof children === "string") {
    // Split by <br> and interleave with <br /> elements
    const parts = children.split(/<br\s*\/?>/i);
    if (parts.length === 1) {
      return children;
    }
    const result: React.ReactNode[] = [];
    for (let i = 0; i < parts.length; i++) {
      if (parts[i]) {
        result.push(parts[i]);
      }
      if (i < parts.length - 1) {
        result.push(<br key={`br-${i}`} />);
      }
    }
    return result;
  }

  if (Array.isArray(children)) {
    return children.flatMap((child, index) => {
      const processed = processBrTagsInChildren(child);
      // If processed is an array, spread it; otherwise wrap in array
      return Array.isArray(processed) ? processed : [processed];
    });
  }

  if (React.isValidElement(children) && children.props.children) {
    return React.cloneElement(children, {
      ...children.props,
      children: processBrTagsInChildren(children.props.children),
    } as any);
  }

  return children;
};

// The concatenated text of a hast node, for inspecting what a table's header
// actually says (#11426).
const hastText = (node: Element | Element["children"][number]): string => {
  if (node.type === "text") return node.value;
  if ("children" in node) return node.children.map(hastText).join("");
  return "";
};

// A one-record × many-fields table announces itself by a header that names the
// concept of a table instead of its subject — "Metric | Value" and its
// synonyms. Such a table renders as the fact block (#11426): the header row is
// hidden and the rows read as label/value facts. A table whose header names
// real columns keeps the comparison grammar.
// Deliberately narrow: every member must name the CONCEPT of a labelled fact,
// never a plausible real column ("Item", "Name"), because a match hides the
// header row.
const GENERIC_FACT_LABELS = new Set([
  "metric",
  "field",
  "attribute",
  "property",
  "key",
  "parameter",
  "characteristic",
]);
const GENERIC_FACT_VALUES = new Set([
  "value",
  "values",
  "amount",
  "detail",
  "details",
]);

const isFactTable = (node?: Element): boolean => {
  if (!node) return false;
  const thead = node.children.find(
    (child) => child.type === "element" && child.tagName === "thead"
  );
  if (!thead || thead.type !== "element") return false;
  const headRow = thead.children.find(
    (child) => child.type === "element" && child.tagName === "tr"
  );
  if (!headRow || headRow.type !== "element") return false;
  const cells = headRow.children.filter(
    (child) => child.type === "element" && child.tagName === "th"
  );
  if (cells.length !== 2) return false;
  const [label, value] = cells.map((cell) =>
    hastText(cell).trim().toLowerCase()
  );
  return GENERIC_FACT_LABELS.has(label) && GENERIC_FACT_VALUES.has(value);
};

// True when a table cell holds nothing renderable — no text and no element
// besides a bare <br> (#11426, rule 5).
const cellIsEmpty = (node?: Element): boolean => {
  if (!node) return false;
  if (hastText(node).trim() !== "") return false;
  return !node.children.some(
    (child) => child.type === "element" && child.tagName !== "br"
  );
};

/**
 * Checks if a citation is a matrix chat cell reference and handles it appropriately.
 * Matrix chat can reference cells from the matrix grid, and these need special handling
 * to open the cell details panel instead of the document panel.
 *
 * @param citationId - The citation identifier (e.g., "1000.1")
 * @param queryId - The query ID to look up the citation source
 * @param parentElementText - The text context around the citation
 * @returns true if this was a matrix cell citation and was handled, false if it's a regular document citation
 */
export const checkAndHandleMatrixCellCitation = async (
  citationId: string,
  queryId: string,
  parentElementText: string
): Promise<boolean> => {
  try {
    // Get the sourcemap for this query
    const sourceMap = await getSourceMapForQid(queryId);
    const citationKey = `[${citationId}]`;
    const citationData = sourceMap[citationKey];

    if (
      citationData &&
      Array.isArray(citationData) &&
      citationData.length > 0
    ) {
      const firstCitation = citationData[0];

      // Check if this is a matrix cell citation
      // We need to check if it's a FullReference (has content and parentDocId) vs CheapReference
      const hasContentAndParentDocId =
        "content" in firstCitation && "parentDocId" in firstCitation;

      if (hasContentAndParentDocId) {
        const isMatrixCellCitation =
          firstCitation.content?.startsWith("Cell:") &&
          firstCitation.parentDocId;

        if (isMatrixCellCitation) {
          // Parse cell coordinates from the content
          const cellCoordinates = parseCellCoordinates(firstCitation.content);

          if (cellCoordinates) {
            // Validate that the parentDocId actually exists as a workspace in MongoDB
            try {
              const workspaceData = await fetchWorkspace(
                firstCitation.parentDocId
              );

              // If workspaceData exists and has content, it's a valid workspace
              if (workspaceData && Object.keys(workspaceData).length > 0) {
                // Dispatch matrix cell highlight event instead of document panel
                window.dispatchEvent(
                  new CustomEvent("highlightMatrixCell", {
                    detail: {
                      citationId,
                      queryId,
                      parentDocId: firstCitation.parentDocId,
                      parentElementText: parentElementText,
                      letterCoord: cellCoordinates.letterCoord,
                      numberCoord: cellCoordinates.numberCoord,
                    },
                  })
                );
                return true;
              }
            } catch (error) {
              console.error(
                "Workspace validation failed for parentDocId:",
                firstCitation.parentDocId,
                error
              );
              // If workspace validation fails, treat it as a regular document citation
            }
          }
        }
      }
    }
  } catch (error) {
    console.error("Error checking matrix cell citation:", error);
  }
  return false;
};

const CODE_FONT = '"IBM Plex Mono", ui-monospace, Menlo, monospace';

// `oneDark` hard-codes `white-space: pre` and `word-break: normal`, and
// react-syntax-highlighter merges the theme AFTER its own `wrapLongLines`
// default — so wrapping only takes effect when these are passed back in
// explicitly. `overflow-wrap: anywhere` rather than `break-word` because only
// `anywhere` also shrinks the block's min-content width, and min-content is
// what a flex ancestor with the default `min-width: auto` sizes to: without it
// a long unbroken token still widens the whole message column (#11409).
const CODE_WRAPPING: React.CSSProperties = {
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
  wordBreak: "normal",
};

// The box itself, built on the theme's own container so a raw `<pre>` fallback
// and a fenced block look identical. Sized in `em` throughout: the renderer is
// mounted at two scales (an answer, and a matrix cell under `.compact-markdown`
// at 0.75rem), and a `rem` size would make a code block in a cell larger than
// the text around it. The theme's `overflow: auto` is deliberately left in
// place — nothing on the highlighter path can overflow it, and on the raw
// `<pre>` fallback below, whose contents this component does not control,
// scrolling is the right degradation and clipping is not.
const CODE_BLOCK_STYLE: React.CSSProperties = {
  ...(oneDark['pre[class*="language-"]'] as React.CSSProperties),
  ...CODE_WRAPPING,
  fontFamily: CODE_FONT,
  fontSize: "0.8125em",
  // Its own vertical rhythm: the root's `space-y-4` sets `margin-top` through a
  // class, which an inline style from the theme would beat anyway.
  margin: "1em 0",
  padding: "0.75em 1em",
  borderRadius: "0.5rem",
};

const CODE_TAG_STYLE: React.CSSProperties = {
  ...CODE_WRAPPING,
  fontFamily: CODE_FONT,
};

// A fenced code block. Both shapes go through the highlighter so they share one
// box; a fence with no info string passes "text", which the highlighter
// short-circuits to unhighlighted output. `wrapLongLines` is deliberately not
// set: `CODE_TAG_STYLE` already carries the wrapping (and wins over it), and
// the prop's only remaining effect would be an extra span per line — churn on
// every token delta while an answer streams.
const CodeBlock = ({ language, text }: { language?: string; text: string }) => (
  <SyntaxHighlighter
    language={language ?? "text"}
    style={oneDark}
    customStyle={CODE_BLOCK_STYLE}
    codeTagProps={{ style: CODE_TAG_STYLE }}
  >
    {text}
  </SyntaxHighlighter>
);

// Null unless every child is literal text. A fenced block always is — the
// citation plugins only rewrite mdast `text` nodes and a fence is a literal
// node with no children — so anything else arrived as raw `<pre>` HTML and is
// rendered as-is instead of being flattened into a string.
const plainTextOf = (children: React.ReactNode): string | null => {
  const parts: string[] = [];
  let literal = true;
  React.Children.forEach(children, (child) => {
    if (typeof child === "string" || typeof child === "number") {
      parts.push(String(child));
    } else if (child !== null && child !== undefined) {
      literal = false;
    }
  });
  return literal ? parts.join("") : null;
};

// react-markdown v9 no longer passes an `inline` prop, and a fence with no info
// string yields a `code` element with no className — nothing on the node itself
// tells a block apart from inline code. The `pre` wrapper is the only signal,
// so blocks are read out of it here and the `code` override handles inline code
// only.
const fencedCodeOf = (
  children: React.ReactNode
): { language?: string; text: string } | null => {
  const [only, ...rest] = React.Children.toArray(children);
  if (
    rest.length > 0 ||
    !React.isValidElement<{ className?: string; children?: React.ReactNode }>(
      only
    )
  ) {
    return null;
  }
  const { className, children: codeChildren } = only.props;
  const text = plainTextOf(codeChildren);
  if (text === null) {
    return null;
  }
  return {
    language: /language-(\w+)/.exec(className ?? "")?.[1],
    text: text.replace(/\n$/, ""),
  };
};

const MarkdownRenderer = ({
  content,
  queryId,
  onCitationClick,
  status,
  isUserMessage = false,
  forceShowCitations = false,
  chatSourceMap,
  citationNumbers,
  inlineViewerOnly = false,
}: MarkdownRendererProps) => {
  // The element map is built once per set of the values its closures read,
  // not once per render. Each entry is a component, and React compares
  // component functions by identity: a map rebuilt on every render makes
  // every citation chip, link and code block a new component type, so a
  // host re-render unmounted and remounted all of them — and a click whose
  // pointer went down on the old node landed on nothing (#11987). The
  // source map and citation numbers are not read here; they go to the
  // remark plugin below, which re-parses on every render anyway.
  const components = React.useMemo((): Components => {
    const components: Components = {
      pre: ({ node, children, ...props }: ElementProps) => {
        const fenced = fencedCodeOf(children);
        const { ref, ...restProps } = props; // Remove ref to avoid type conflicts
        return fenced ? (
          <CodeBlock language={fenced.language} text={fenced.text} />
        ) : (
          // Raw `<pre>` HTML in an answer: same box, element and contents
          // otherwise left as the answer wrote them.
          <pre {...restProps} style={CODE_BLOCK_STYLE}>
            {children}
          </pre>
        );
      },
      // Inline code only — fenced blocks are consumed by `pre` above.
      code({ node, className, children, ...props }: CodeProps) {
        const { ref, ...restProps } = props; // Remove ref to avoid type conflicts
        return (
          <code
            {...restProps}
            className={`rounded bg-gray-100 px-1 py-0.5 text-[0.9em] text-gray-900 break-words ${className ?? ""}`.trim()}
            style={{ fontFamily: CODE_FONT }}
          >
            {children}
          </code>
        );
      },
      p: ({ children }: ElementProps) => <p className="mb-4">{children}</p>,
      h1: ({ children }: ElementProps) => (
        <h1 className="text-2xl font-bold mb-4">{children}</h1>
      ),
      h2: ({ children }: ElementProps) => (
        <div className="mb-4">
          <h2 className="text-xl font-bold">{children}</h2>
        </div>
      ),
      h3: ({ children }: ElementProps) => (
        <div className="mb-4">
          <h3 className="text-lg font-bold">{children}</h3>
        </div>
      ),
      h4: ({ children }: ElementProps) => (
        <h4 className="text-base font-semibold mb-3">{children}</h4>
      ),
      h5: ({ children }: ElementProps) => (
        <h5 className="text-sm font-semibold mb-2">{children}</h5>
      ),
      h6: ({ children }: ElementProps) => (
        <h6 className="text-xs font-semibold uppercase tracking-wide mb-1">
          {children}
        </h6>
      ),
      ul: ({ children }: ElementProps) => (
        <div className="mb-4 ml-8">
          <ul className="list-disc space-y-2">{children}</ul>
        </div>
      ),
      ol: ({ children }: ElementProps) => (
        <div className="mb-4 ml-8">
          <ol className="list-decimal space-y-2">{children}</ol>
        </div>
      ),
      li: ({ children }: ElementProps) => (
        <li className="pl-2">
          <div className="mb-1">{children}</div>
        </li>
      ),
      table: ({ node, children }: ElementProps) => (
        // A one-record table announcing itself by a generic header ("Metric |
        // Value") renders as the fact block — header hidden, label/value rows —
        // while a subject-headed table keeps the comparison grammar (#11426).
        // The `fact-table` class only means something where the chat table
        // stylesheet (PrettierTables' TableStylesComponent) is mounted; on any
        // other surface it is inert and the table renders as before.
        <div
          className={`table-wrapper${isFactTable(node) ? " fact-table" : ""}`}
        >
          <table className="w-auto max-w-full border-collapse bg-white text-sm">
            {children}
          </table>
        </div>
      ),
      // `break-words` opts cells out of the root's `overflow-wrap: anywhere`: a
      // column's width comes from its content's min-content, and `anywhere` would
      // let every column collapse to one character.
      th: ({ children }: ElementProps) => (
        <th className="bg-gray-50 font-semibold text-left p-3 border-b-2 border-gray-200 text-gray-700 break-words">
          {children}
        </th>
      ),
      td: ({ node, children }: ElementProps) => (
        <td className="p-3 border-b border-gray-200 text-gray-700 break-words">
          {cellIsEmpty(node) ? (
            // Rule 5 (#11426): an absent value is an em dash, never a blank cell.
            <span className="cell-none text-gray-400">—</span>
          ) : (
            processBrTagsInChildren(children)
          )}
        </td>
      ),
      a: ({ href, children, ...props }: any) => {
        // Check if this is a citation link with the expected format
        if (href && href.includes("cid=")) {
          return (
            <CitationLink
              href={href}
              status={status}
              queryId={queryId}
              inlineViewerOnly={inlineViewerOnly}
            >
              {children}
            </CitationLink>
          );
        }
        // An RMS record in Finpilot (#12430): the chip an @-mention renders,
        // which opens the record beside the answer, rather than a link that
        // leaves the conversation.
        const record = recordFromHref(href);
        if (record) {
          return (
            <RecordMentionChip
              layout="inline"
              kind={record.kind}
              id={record.id}
              name={childrenText(children) || record.id}
            />
          );
        }
        // A document in Finpilot: opened in the viewer beside the answer rather
        // than followed out of the app (#11441).
        const documentId = documentIdFromHref(href);
        if (documentId) {
          return (
            <DocumentLink
              documentId={documentId}
              status={status}
              inlineViewerOnly={inlineViewerOnly}
            >
              {children}
            </DocumentLink>
          );
        }
        // Regular link
        return (
          <a
            href={href}
            {...props}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 transition-colors"
            style={{ fontWeight: "inherit" }}
          >
            {children}
          </a>
        );
      },
    } as Components & {
      // Custom citation component
      "citation-button": ({
        citationId,
        queryId,
      }: CitationButtonElementProps) => React.JSX.Element;
    };

    // Add the custom citation component separately to avoid type conflicts
    (components as any)["citation-button"] = ({
      citationId,
      displayNumber,
      hidden,
    }: CitationButtonElementProps & { hidden?: boolean }) => {
      return (
        <CitationButton
          citationId={citationId}
          queryId={queryId!}
          displayNumber={displayNumber}
          onClick={onCitationClick}
          status={status}
          hidden={hidden}
        />
      );
    };

    // Agentic-chat citation chip: resolved against the per-message chatSourceMap
    // (legacy `[NNNNN.M]` markers) rather than a fetch-on-click.
    (components as any)["chat-citation"] = ({
      markerKey,
      docId,
      publicId,
      kind,
      factName,
      interactionId,
      artifactId,
      webUrl,
      iodlPayload,
      calculationPayload,
      pageNum,
      sourceContent,
      displayLabel,
      displayNumber,
      displayText,
    }: ChatCitationElementProps) => {
      return (
        <ChatCitationButton
          markerKey={markerKey}
          docId={docId}
          publicId={publicId}
          kind={kind}
          factName={factName}
          interactionId={interactionId}
          artifactId={artifactId}
          webUrl={webUrl}
          iodlPayload={iodlPayload}
          calculation={parseCalculationPayload(calculationPayload)}
          iodlQid={queryId}
          pageNum={
            Number.isFinite(Number(pageNum)) ? Number(pageNum) : undefined
          }
          sourceContent={sourceContent}
          displayLabel={displayLabel}
          displayNumber={displayNumber}
          displayText={displayText}
          status={status}
          inlineViewerOnly={inlineViewerOnly}
        />
      );
    };

    // A collapsed run of 3+ agentic-chat chip citations (legacy MultipleCitations
    // parity), resolved against the per-message map rather than a qid.
    (components as any)["chat-multiple-citations"] = ({
      citations,
    }: {
      citations: string;
    }) => {
      try {
        const parsed = JSON.parse(citations) as ChatGroupedCitation[];
        return (
          <ChatMultipleCitationsButton
            citations={parsed}
            status={status}
            inlineViewerOnly={inlineViewerOnly}
          />
        );
      } catch (error) {
        console.error("Error parsing chat citations:", error);
        return null;
      }
    };

    // Add the custom multiple citations component
    (components as any)["multiple-citations"] = ({
      citations,
    }: MultipleCitationsElementProps) => {
      try {
        const citationsArray = JSON.parse(citations);
        return (
          <MultipleCitationsButton
            citations={citationsArray}
            queryId={queryId!}
            onClick={onCitationClick}
            status={status}
          />
        );
      } catch (error) {
        console.error("Error parsing citations:", error);
        return null;
      }
    };
    return components;
  }, [status, queryId, inlineViewerOnly, onCitationClick]);

  // Clean malformed markdown links before rendering
  const cleanedContent = isUserMessage
    ? content
    : cleanMalformedMarkdownLinks(content);

  return (
    // A long unbroken token in prose (a URL, a path) breaks rather than pushing
    // the answer column wide (#11409). `anywhere` rather than `break-word`
    // because only `anywhere` also shrinks min-content, and min-content is what
    // a flex ancestor with the default `min-width: auto` sizes to — with
    // `break-word` the line breaks but the column still widens. Table cells opt
    // back out below, since their columns size from their content.
    <div className="max-w-none markdown-renderer [overflow-wrap:anywhere]">
      {isUserMessage ? (
        // A user's message is plain text by design (markdown must not
        // reinterpret what they typed); `whitespace-pre-wrap` keeps their
        // newlines, and bare URLs still render as links (#12201).
        <div className="whitespace-pre-wrap" style={{ whiteSpace: "pre-wrap" }}>
          {linkifyText(cleanedContent)}
        </div>
      ) : (
        // For assistant messages, use ReactMarkdown for proper markdown processing
        <ReactMarkdown
          remarkPlugins={[
            // Disable single-tilde strikethrough so a lone `~` used to mean
            // "approximately" (e.g. `~($663 million)`) renders literally
            // instead of striking through to the next tilde. Double-tilde
            // `~~strikethrough~~` still works.
            [remarkGfm, { singleTilde: false }] as [Plugin, object],
            // The agentic-chat (chatSourceMap) path and the legacy qid path are
            // mutually exclusive — chatSourceMap takes precedence so the
            // agentic answer never also runs the qid-based remarkCitations.
            ...(chatSourceMap
              ? [
                  [
                    remarkChatCitations,
                    { sourceMap: chatSourceMap, numbers: citationNumbers },
                  ] as [Plugin, object],
                ]
              : queryId
                ? [
                    [remarkCitations, { forceShowCitations }] as [
                      Plugin,
                      object,
                    ],
                  ]
                : []),
          ]}
          rehypePlugins={[rehypeRaw]}
          className="space-y-4"
          components={components}
        >
          {cleanedContent.trim()}
        </ReactMarkdown>
      )}
    </div>
  );
};

// Memoized: during streaming the chat re-renders on every token delta and maps
// each text chunk to a MarkdownRenderer. Without memo, every delta re-parses the
// entire answer's markdown (all chunks) — O(n²) stutter. With it, unchanged
// earlier chunks bail out, so only the growing last chunk re-renders. #8147.
// The bail-out holds only while every prop keeps its identity: the source map
// and citation numbers are objects, so a host that rebuilds them each render
// re-parses each render (the element map above stays put regardless).
export default React.memo(MarkdownRenderer);

interface CitationButtonProps {
  citationId: string;
  queryId: string;
  displayNumber: number;
  onClick?: (citationId: string) => void;
  status?: ChatMessageStatus;
  parentElementText?: string;
  hidden?: boolean;
}

export const CitationButton: React.FC<CitationButtonProps> = ({
  citationId,
  queryId,
  displayNumber,
  onClick,
  status,
  parentElementText,
  hidden,
}) => {
  const getParentElementText = (e: React.MouseEvent<HTMLSpanElement>) => {
    const parent = (e.target as HTMLSpanElement)?.parentElement;
    if (!parent) return "";
    const parentNodeCopy = parent.cloneNode(true) as HTMLElement;
    const citationSpans = parentNodeCopy.querySelectorAll(".inline-flex");
    citationSpans.forEach((span) => span.remove());
    return parentNodeCopy.textContent?.trim() || "";
  };

  const handleClick = async (e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Don't allow clicks while streaming or if hidden
    if (status === "streaming" || hidden) {
      return;
    }

    const computedParentElementText =
      parentElementText || getParentElementText(e);

    // Check if this citation references a matrix cell (only relevant for matrix chat)
    const isMatrixCellCitation = await checkAndHandleMatrixCellCitation(
      citationId,
      queryId,
      computedParentElementText
    );
    if (isMatrixCellCitation) {
      onClick?.(citationId);
      return;
    }

    // Default behavior: Dispatch window event to open document panel
    window.dispatchEvent(
      new CustomEvent("openDocumentPanel", {
        detail: {
          citationId,
          queryId,
          parentElementText: computedParentElementText,
        },
      })
    );

    // Still call the original onClick if provided
    onClick?.(citationId);
  };

  // If hidden, render an invisible element that takes no space
  if (hidden) {
    return (
      <span
        style={{ display: "none" }}
        data-citation-id={citationId}
        data-display-number={displayNumber}
      />
    );
  }

  return (
    <span
      onClick={handleClick}
      className={`inline-flex justify-center items-center w-4 h-4 text-[10px] align-text-top ${
        status === "streaming"
          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
          : "bg-blue-200 hover:bg-blue-300 text-blue-900 cursor-pointer"
      } rounded-full transition-colors`}
      title={
        status === "streaming"
          ? "Citation generating..."
          : `Citation ${citationId}`
      }
    >
      {displayNumber}
    </span>
  );
};

interface ChatCitationButtonProps {
  displayLabel: string;
  displayNumber: number;
  // The bracketed marker this chip stands for. With a passage source in
  // context (an agent answer, #12081), a document chip whose entry carries no
  // passage fetches it before it opens.
  markerKey?: string;
  displayText?: string;
  // Exactly one open-target discriminator is set:
  // - docId: open the cited document
  // - publicId + kind: open the RMS entity preview, on `factName` when the
  //   citation names one attribute of the record (#11369)
  // - interactionId: open the RMS interaction viewer
  // - webUrl: open the URL in a new tab
  // - iodlPayload: open the IODL source viewer (JSON: sql/columns/rows/...)
  docId?: string;
  publicId?: string;
  kind?: string;
  factName?: string;
  interactionId?: string;
  artifactId?: string;
  webUrl?: string;
  iodlPayload?: string;
  iodlQid?: string;
  // A recorded calculation (#13618): the chip (or its figure) opens the
  // calculation card beside itself instead of any viewer.
  calculation?: ChatCalculationSource;
  // Evidence anchor for document citations (#10310): harvested page number +
  // verbatim chunk text, forwarded to the viewer as highlightPageNum /
  // highlightContext so the click lands on the cited passage.
  pageNum?: number;
  sourceContent?: string;
  status?: ChatMessageStatus;
  // The host page mounts its own `<FileViewer />`; ask Layout to fill the
  // shared viewer state without also mounting the global viewer. See
  // MarkdownRendererProps.inlineViewerOnly.
  inlineViewerOnly?: boolean;
  // Highlight the value where the page prints it, or nothing (#12090); see
  // ChatCitationTarget.exactOnly. A table cell sets it.
  exactOnly?: boolean;
  // Open on the cited passage alone (#14543); see
  // ChatCitationTarget.passageOnly. A table cell sets it.
  passageOnly?: boolean;
  // The page's spelling of the figure, when it differs from displayText
  // (#12452); see ChatCitationTarget.highlightText.
  highlightText?: string;
  // The citation sits in an artifact pane (#12452); see
  // ChatCitationTarget.fromArtifact. A table cell sets it.
  fromArtifact?: boolean;
  // Told when the calculation card this chip draws opens and closes, so a
  // collapsed group holding the chip stays expanded under it (#13618).
  onCardOpenChange?: (open: boolean) => void;
}

// Agentic-chat citation chip (legacy `[NNNNN.M]` scheme + self-describing
// `[[web:url]]` markers). Unlike the legacy qid CitationButton it carries its
// resolved label inline (no fetch-on-click). Its open target depends on which
// discriminator is set: a document (by blob id), an RMS entity preview, an RMS
// interaction (its viewer), or a web URL (new tab). When `displayText` is set
// (the marker followed a figure), it renders that figure as blue clickable
// text inline instead of a trailing number — matching the legacy chat's
// NumericCitationNode style.
/** The calculation a chip's element props carry, parsed back at the boundary. */
const parseCalculationPayload = (
  payload?: string
): ChatCalculationSource | undefined => {
  if (!payload) return undefined;
  try {
    return JSON.parse(payload) as ChatCalculationSource;
  } catch (error) {
    console.error("Error parsing calculation citation payload:", error);
    return undefined;
  }
};

export const ChatCitationButton: React.FC<ChatCitationButtonProps> = ({
  markerKey,
  docId,
  publicId,
  kind,
  factName,
  interactionId,
  artifactId,
  webUrl,
  iodlPayload,
  iodlQid,
  calculation,
  pageNum,
  sourceContent,
  displayLabel,
  displayNumber,
  displayText,
  status,
  inlineViewerOnly = false,
  exactOnly = false,
  passageOnly = false,
  highlightText,
  fromArtifact = false,
  onCardOpenChange,
}) => {
  // The text around this citation, minus any citation chips — used to find and
  // highlight the cited passage in the opened PDF (same mechanism as the legacy
  // chat's parentElementText).
  const surroundingText = (
    parent: globalThis.Element | null | undefined
  ): string => {
    if (!parent) return "";
    const copy = parent.cloneNode(true) as HTMLElement;
    copy.querySelectorAll(".fp-chat-citation").forEach((n) => n.remove());
    return copy.textContent?.trim() || "";
  };

  // The structured-data payload rode through the element props as one JSON
  // string; parse it back once, here at the boundary, into the typed shape.
  const iodlSource = (): ChatIodlSource | undefined => {
    if (!iodlPayload) return undefined;
    try {
      return JSON.parse(iodlPayload) as ChatIodlSource;
    } catch (error) {
      console.error("Error parsing IODL citation payload:", error);
      return undefined;
    }
  };

  const passages = React.useContext(CitationPassagesContext);
  // A document chip whose entry left its passage out (an agent answer's
  // history read, #12081) asks for it as soon as the reader points at it.
  const wantsPassage =
    !iodlPayload &&
    needsPassage({
      docId,
      publicId,
      kind,
      interactionId,
      artifactId,
      webUrl,
      sourceContent,
    });
  const prefetchPassage = () => {
    if (wantsPassage && passages && markerKey) passages.prefetch(markerKey);
  };

  // A calculation opens its card anchored to what was clicked (#13618).
  const [cardAnchor, setCardAnchorState] = React.useState<HTMLElement | null>(
    null
  );
  const setCardAnchor = (anchor: HTMLElement | null) => {
    setCardAnchorState(anchor);
    onCardOpenChange?.(anchor !== null);
  };

  const open = (e?: React.SyntheticEvent) => {
    if (status === "streaming") return;
    const target = e?.currentTarget as HTMLElement;
    if (calculation) {
      setCardAnchor(target ?? null);
      return;
    }
    void openChatCitationWithPassage(
      {
        docId,
        publicId,
        kind,
        factName,
        interactionId,
        artifactId,
        webUrl,
        iodlSource: iodlSource(),
        iodlQid,
        pageNum,
        sourceContent,
        displayLabel,
        displayText,
        highlightText,
        exactOnly,
        passageOnly,
        fromArtifact,
        context: () => surroundingText(target?.parentElement),
        rowLabel: () =>
          surroundingText(target?.closest("tr")?.querySelector("td, th")),
        inlineViewerOnly,
      },
      wantsPassage && passages && markerKey
        ? () => passages.passageFor(markerKey)
        : undefined
    );
  };

  const label = displayLabel || "Open cited source";
  const ariaLabel = displayLabel ? `Source: ${label}` : label;
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      open(e);
    }
  };
  const card = calculation ? (
    <CalculationCard
      anchorEl={cardAnchor}
      onClose={() => setCardAnchor(null)}
      calculation={calculation}
      citationQueryId={iodlQid}
      inlineViewerOnly={inlineViewerOnly}
    />
  ) : null;
  const popupProps = calculation
    ? {
        "aria-haspopup": "dialog" as const,
        "aria-expanded": Boolean(cardAnchor),
      }
    : {};

  // Inline-figure mode: render the cited figure (e.g. "32.7%") as clickable
  // ink — the numeric badge beside it carries the citation state (CD,
  // gaps-v9 review: blue as the accent decorating figures was the legacy
  // vocabulary; on the redesigned surfaces state is ink).
  if (displayText) {
    const figure = (
      <span
        role="button"
        tabIndex={0}
        aria-label={`Source: ${displayLabel || displayText}`}
        {...popupProps}
        onClick={open}
        onKeyDown={onKeyDown}
        onMouseEnter={prefetchPassage}
        onFocus={prefetchPassage}
        className={`fp-chat-citation ${
          status === "streaming"
            ? "text-gray-400 cursor-default"
            : // Ink, like every state on the redesigned surfaces — but with a
              // mark of its own: the chip this figure replaced is suppressed,
              // so without one a cited figure read as plain prose (#11825).
              "cursor-pointer text-[var(--fp-ink)] underline decoration-dotted underline-offset-2"
        }`}
      >
        {displayText}
      </span>
    );
    return card ? (
      <>
        {figure}
        {card}
      </>
    ) : (
      figure
    );
  }

  const chip = (
    <span
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      {...popupProps}
      onClick={open}
      onKeyDown={onKeyDown}
      onMouseEnter={prefetchPassage}
      onFocus={prefetchPassage}
      className={`fp-chat-citation inline-flex justify-center items-center w-4 h-4 text-[10px] align-text-top ${
        status === "streaming"
          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
          : "bg-blue-200 hover:bg-blue-300 text-blue-900 cursor-pointer"
      } rounded-full transition-colors`}
    >
      {displayNumber}
    </span>
  );
  return card ? (
    <>
      {chip}
      {card}
    </>
  ) : (
    chip
  );
};

// One grouped chip inside a ChatMultipleCitations dropdown — the chip's
// hProperties minus the inline-figure fields (a grouped chip is never a figure).
interface ChatGroupedCitation {
  displayLabel: string;
  displayNumber: number;
  markerKey?: string;
  docId?: string;
  publicId?: string;
  kind?: string;
  factName?: string;
  interactionId?: string;
  artifactId?: string;
  webUrl?: string;
  pageNum?: number;
  sourceContent?: string;
  calculationPayload?: string;
}

interface ChatMultipleCitationsButtonProps {
  citations: ChatGroupedCitation[];
  status?: ChatMessageStatus;
  // Forwarded to each expanded chip. See MarkdownRendererProps.inlineViewerOnly.
  inlineViewerOnly?: boolean;
}

// A collapsed run of citations at rest is ONE numeric badge carrying the
// count — the same anatomy as a single citation chip, a little wider
// (#11426 gaps-v9 C2). The tinted "N citations" pill it replaces was the
// legacy chat's vocabulary restating the badge louder; within the redesign
// an answer paragraph shows exactly one citation anatomy at rest. The badge
// expands to the individual chips on focus, click, or tap — never hover
// alone (CD ruling: the count must be reachable without a pointer) — and
// closes on Escape or when focus leaves the group. Each expanded chip opens
// its own target (document / entity / web) via ChatCitationButton. No qid —
// the open-targets are carried inline from the per-message map.
export const ChatMultipleCitationsButton: React.FC<
  ChatMultipleCitationsButtonProps
> = ({ citations, status, inlineViewerOnly = false }) => {
  const [showCitations, setShowCitations] = React.useState(false);
  // A calculation chip in the group draws its card in a portal: focus moving
  // into the card, and Escape closing it, are the card's, never a reason to
  // collapse the group out from under it (#13618).
  const [cardOpen, setCardOpen] = React.useState(false);

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (status === "streaming") return;
    setShowCitations((v) => !v);
  };

  return (
    <span
      className="inline-flex items-center gap-1"
      onBlur={(e) => {
        if (cardOpen) return;
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setShowCitations(false);
        }
      }}
      onKeyDown={(e) => {
        if (cardOpen) return;
        if (e.key === "Escape") setShowCitations(false);
      }}
    >
      <span
        role="button"
        tabIndex={0}
        aria-expanded={showCitations}
        aria-label={
          status === "streaming"
            ? "Citations generating"
            : `${citations.length} citations`
        }
        onClick={handleToggle}
        // A mouse press must not focus the badge: focus-opens plus
        // click-toggles would open and immediately re-close on the same
        // click. With focus suppressed here, onFocus fires only for
        // keyboard reach — which is the accessibility the ruling requires.
        onMouseDown={(e) => e.preventDefault()}
        onFocus={() => {
          if (status !== "streaming") setShowCitations(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (status !== "streaming") setShowCitations((v) => !v);
          }
        }}
        className={`inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] align-text-top font-medium ${
          status === "streaming"
            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
            : "bg-blue-200 hover:bg-blue-300 text-blue-900 cursor-pointer"
        } transition-colors`}
      >
        {citations.length}
      </span>
      {showCitations && (
        <span className="inline-flex gap-1 flex-wrap">
          {citations.map((citation, idx) => (
            <ChatCitationButton
              key={`${citation.displayNumber}-${idx}`}
              markerKey={citation.markerKey}
              docId={citation.docId}
              publicId={citation.publicId}
              kind={citation.kind}
              factName={citation.factName}
              interactionId={citation.interactionId}
              artifactId={citation.artifactId}
              webUrl={citation.webUrl}
              pageNum={citation.pageNum}
              sourceContent={citation.sourceContent}
              calculation={parseCalculationPayload(citation.calculationPayload)}
              onCardOpenChange={setCardOpen}
              displayLabel={citation.displayLabel}
              displayNumber={citation.displayNumber}
              status={status}
              inlineViewerOnly={inlineViewerOnly}
            />
          ))}
        </span>
      )}
    </span>
  );
};

interface CitationLinkProps {
  href: string;
  status?: ChatMessageStatus;
  queryId?: string;
  /** The page draws its own split-pane viewer (named agents); Layout fills
   *  the shared viewer state without mounting the global viewer too. */
  inlineViewerOnly?: boolean;
  children: React.ReactNode;
}

/** The piece of an annotated link's `match` param this link is actually
 *  about. A row-level annotated citation carries every figure of its table
 *  row pipe-joined ("378.8m | 2.95% | $549.3m | $170.5m"); sent whole, the
 *  viewer's matcher can land on a SIBLING cell's figure (#11496 — clicking
 *  $378.8m highlighted 2.95%). The segment corresponding to the link's own
 *  label — the figure the user clicked — is selected instead, compared with
 *  punctuation stripped so a "$378.8m" label finds the match-side "378.8m".
 *  Anything without a corresponding segment (including every single-figure
 *  match) passes through unchanged. */
const pickMatchSegmentForLabel = (
  match: string | null,
  label: string
): string | null => {
  if (!match || !match.includes("|")) {
    return match;
  }
  const normalize = (s: string) => s.replace(/[^0-9a-z.%]/gi, "").toLowerCase();
  const target = normalize(label);
  if (!target) {
    return match;
  }
  const segments = match
    .split("|")
    .map((s) => s.trim())
    .filter(Boolean);
  return (
    segments.find((s) => normalize(s) === target) ??
    segments.find((s) => {
      const n = normalize(s);
      return n.length > 0 && (target.includes(n) || n.includes(target));
    }) ??
    match
  );
};

export const CitationLink: React.FC<CitationLinkProps> = ({
  href,
  status,
  queryId,
  inlineViewerOnly,
  children,
}) => {
  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Don't allow clicks while streaming
    if (status === "streaming") {
      return;
    }

    // Read the clicked figure BEFORE the first await: React clears
    // `currentTarget` once the synchronous phase of the handler ends, and the
    // matrix-cell check below awaits, so reading it later yields null.
    const clickedText = (e.currentTarget as HTMLElement | null)?.innerText;

    try {
      const url = new URL(href);
      const citationId = url.searchParams.get("cid");
      const matchText = pickMatchSegmentForLabel(
        url.searchParams.get("match"),
        labelText(children)
      );
      // The named-agents page hands the renderer no turn id: its links carry
      // the session's scope themselves (#11825), read off the href by the
      // same parser a delivered document's link goes through.
      const scopeId = queryId ?? parseCitationDeepLink(href)?.queryId;

      if (citationId && scopeId) {
        if (matchText) {
          // Check if this citation references a matrix cell (only relevant for matrix chat)
          const isMatrixCellCitation = await checkAndHandleMatrixCellCitation(
            citationId,
            scopeId,
            matchText
          );
          if (isMatrixCellCitation) return;
        }
        const anchor = citationAnchorValue(clickedText, matchText);
        // The passage line the link's anchor sits on (#14039): the viewer
        // windows its search on it, so a figure the page repeats lands on
        // the cited occurrence. A link minted before it carries none.
        const linkContext = url.searchParams.get("ctx");
        window.dispatchEvent(
          new CustomEvent("openDocumentPanel", {
            detail: {
              citationId,
              queryId: scopeId,
              ...(anchor ? { originalNumValue: anchor } : {}),
              ...(linkContext ? { linkContext } : {}),
              ...(inlineViewerOnly ? { inlineViewerOnly: true } : {}),
            },
          })
        );
      }
    } catch (error) {
      console.error("Error parsing citation link:", error, "href:", href);
    }
  };

  return (
    <span
      onClick={handleClick}
      className={`${
        status === "streaming"
          ? "text-gray-400 cursor-not-allowed"
          : "text-blue-600 hover:text-blue-800 cursor-pointer"
      } transition-colors font-medium inherit`}
      style={{ fontWeight: "inherit" }}
    >
      {children}
    </span>
  );
};

// A figure as an answer spells one, used to tell a clicked figure from a
// clicked label. Anchored at both ends: the whole clicked text must be the
// figure, so a sentence never qualifies.
const CLICKED_FIGURE = /^-?\(?[$£€]?\d[\d,]*(?:\.\d+)?\)?%?[a-zA-Z]{0,3}$/;

/**
 * The single cell value a citation points at, or undefined to open the
 * document without selecting anything.
 *
 * ``inject_citations`` writes EVERY figure on the answer row into the
 * citation URL's ``match`` parameter, pipe-separated
 * (``0|11.57|8.18|11.57|8.46``). Passing that on as the anchor let
 * ``parseFloat`` take the leading number, so a citation selected a cell
 * holding some other figure from its row — measured at 4 of 23 spreadsheet
 * citations in one answer (#11454). The figure the reader clicked is the
 * unambiguous anchor; a list nobody can choose from is not an anchor at all.
 */
export const citationAnchorValue = (
  clickedText: string | null | undefined,
  matchText: string | null | undefined
): string | undefined => {
  const clicked = (clickedText ?? "").trim();
  const match = (matchText ?? "").trim();
  if (CLICKED_FIGURE.test(clicked)) {
    // When a single-valued match states the SAME figure as the clicked
    // label differing only by decoration — currency symbols, grouping
    // commas, spaces — the match side is the spelling that verbatim exists
    // in the source, so it is the anchor the viewer can actually find
    // (#11496: a row link's label "$378.8m" selects the match-side
    // "378.8m"). Signs and % are figure content, not decoration: a label
    // carrying either keeps winning, so "(1.35)" and "8.46%" stay as
    // clicked.
    const decorationStripped = (s: string) =>
      s.replace(/[$£€,\s]/g, "").toLowerCase();
    if (
      match &&
      !match.includes("|") &&
      decorationStripped(match) === decorationStripped(clicked)
    ) {
      return match;
    }
    return clicked;
  }

  if (!match || match.includes("|")) return undefined;
  return match;
};

/** The blob id an href points at, when it addresses the app's document route.
 *
 *  Both forms an answer can carry are accepted — the relative
 *  `/documents?docId=<id>` the agent is told to write, and the absolute
 *  `https://app.finpilotai.com/documents?docId=<id>` the viewer's own share
 *  action produces — so the same answer reads the same way wherever it was
 *  written. Anything else (an external address, `/documents` with no id) is not
 *  a document reference and is left alone. */
// The record kinds a `/rms/<kind>/<id>` link can name. Interactions have
// their own route and viewer and are not record chips.
const RECORD_LINK_KINDS: ReadonlySet<string> = new Set<EntityKind>([
  "person",
  "company",
  "manager",
  "vehicle",
  "topic",
  "client",
  "portfolio",
  "deal",
]);

/** The record an in-app RMS link names (#12430): `/rms/<kind>/<id>` or the
 *  slugged form `/rms/<kind>/<name>-<id>` the app itself writes; the id is
 *  the 12-character tail either way. Null for any other href. */
export const recordFromHref = (
  href: unknown
): { kind: EntityKind; id: string } | null => {
  if (typeof href !== "string") return null;
  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    const match = url.pathname.match(/^\/rms\/([a-z]+)\/([^/]+)\/?$/);
    if (!match || !RECORD_LINK_KINDS.has(match[1])) return null;
    const id = extractTrailingIdFromSlug(decodeURIComponent(match[2]));
    if (!/^[0-9a-f]{12}$/.test(id)) return null;
    return { kind: match[1] as EntityKind, id };
  } catch {
    return null;
  }
};

/** The plain text of a link's children, for the chip's name. */
const childrenText = (children: React.ReactNode): string =>
  React.Children.toArray(children)
    .map((child) =>
      typeof child === "string" || typeof child === "number"
        ? String(child)
        : React.isValidElement<{ children?: React.ReactNode }>(child)
          ? childrenText(child.props.children)
          : ""
    )
    .join("")
    .trim();

export const documentIdFromHref = (href: unknown): string | null => {
  if (typeof href !== "string") {
    return null;
  }
  try {
    const url = new URL(href, window.location.origin);
    if (url.pathname !== "/documents") {
      return null;
    }
    return url.searchParams.get("docId") || null;
  } catch {
    // Not a URL at all (an anchor, a mailto, a bare word) — not our link.
    return null;
  }
};

/** Text of a link's label, for deciding whether the author named the document.
 *  Only string children are considered: a label built out of elements
 *  (emphasis, code) was written by someone who had something to say. */
const labelText = (children: React.ReactNode): string =>
  React.Children.toArray(children)
    .map((child) => (typeof child === "string" ? child : ""))
    .join("");

interface DocumentLinkProps {
  documentId: string;
  status?: ChatMessageStatus;
  inlineViewerOnly?: boolean;
  children: React.ReactNode;
}

/** A reference to a document in Finpilot, rendered as the document rather than
 *  as an address (#11441). Clicking opens it in the document viewer beside the
 *  answer, through the same event the sidebar, the chat file selector and the
 *  `?docId=` deep link already use — the viewer resolves name and type from the
 *  blob id, which is why neither is passed here. */
export const DocumentLink: React.FC<DocumentLinkProps> = ({
  documentId,
  status,
  inlineViewerOnly,
  children,
}) => {
  // Answers written before the naming guidance landed label the link with the
  // address itself, which reads as a path and an opaque id mid-sentence. Their
  // stored text is never rewritten, so the honest label is supplied here.
  const label = labelText(children).includes("docId=") ? (
    "Open document"
  ) : (
    <>{children}</>
  );

  const open = () => {
    if (status === "streaming") {
      return;
    }
    window.dispatchEvent(
      new CustomEvent("openDocumentPanel", {
        detail: {
          fileId: documentId,
          fileName: "",
          fileType: "",
          isDirectFileOpen: true,
          inlineViewerOnly,
        },
      })
    );
  };

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        open();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
      className={`inline items-baseline ${
        status === "streaming"
          ? "text-gray-400 cursor-not-allowed"
          : "text-blue-600 hover:text-blue-800 cursor-pointer"
      } transition-colors`}
      style={{ fontWeight: "inherit" }}
    >
      <File className="inline w-4 h-4 mr-1 align-text-bottom shrink-0" />
      {label}
    </span>
  );
};

import { visit } from "unist-util-visit";
import { Text } from "mdast";
import { Root } from "mdast";
import { Plugin } from "unified";
import {
  findCitableNumerics,
  hasCitableNumericBeforeCitation,
  numericBindsToSource,
} from "../../utils/citationDetection";

interface CitationNode {
  type: "citation";
  data: {
    hName: string;
    hProperties: {
      citationId: string;
      displayNumber: number;
      hidden?: boolean;
    };
  };
  children: never[];
}

interface MultipleCitationsNode {
  type: "multipleCitations";
  data: {
    hName: string;
    hProperties: {
      citations: string; // JSON string of array
    };
  };
  children: never[];
}

/**
 * Remark plugin to process citation patterns in markdown text
 * Converts [1000.1] style citations into custom AST nodes
 * Groups 3+ consecutive citations into a multiple citations dropdown
 */
export const remarkCitations: Plugin<[Record<string, unknown>?], Root> = (
  options = {}
) => {
  let displayNumber = 1;
  let citationToDisplayNum: Record<string, number> = {};
  const forceShowCitations = !!options.forceShowCitations;
  return (tree) => {
    visit(tree, "text", (node: Text, index, parent) => {
      // Updated regex to match one or more citations separated by commas inside brackets
      const citationRegex = new RegExp(CITATION_REGEX.source, "g");

      if (!citationRegex.test(node.value)) {
        return;
      }

      // Reset regex for processing
      citationRegex.lastIndex = 0;

      const newNodes: (Text | CitationNode)[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = citationRegex.exec(node.value)) !== null) {
        const [fullMatch, citationList] = match;
        const matchStart = match.index;

        // Check if the citation is preceded by a citable numeric, and if so, hide it.
        // Source-modal contexts opt out so a body like "801-73745 [1123.1]"
        // still renders a visible, clickable chip — without surrounding
        // prose there's nothing else to anchor the click on.
        const textBeforeCitation = node.value.slice(0, matchStart);
        const shouldHideCitation =
          !forceShowCitations &&
          hasCitableNumericBeforeCitation(textBeforeCitation);

        // Add text before citation if any
        if (matchStart > lastIndex) {
          const textBefore = node.value.slice(lastIndex, matchStart);
          if (textBefore) {
            newNodes.push({
              type: "text",
              value: textBefore,
            });
          }
        }

        // Split the citationList by comma and process each citation
        const citationIds = citationList
          .split(",")
          .map((id) => id.trim())
          .filter(Boolean);
        for (const citationId of citationIds) {
          if (!citationToDisplayNum[citationId]) {
            citationToDisplayNum[citationId] = displayNumber;
            displayNumber++;
          }
          newNodes.push({
            type: "citation",
            data: {
              hName: "citation-button",
              hProperties: {
                citationId: citationId,
                displayNumber: citationToDisplayNum[citationId],
                hidden: shouldHideCitation,
              },
            },
            children: [],
          });
        }

        lastIndex = matchStart + fullMatch.length;
      }

      // Add remaining text after last citation
      if (lastIndex < node.value.length) {
        const remainingText = node.value.slice(lastIndex);
        if (remainingText) {
          newNodes.push({
            type: "text",
            value: remainingText,
          });
        }
      }

      // Group consecutive citations (3 or more) into multiple citations nodes
      const finalNodes: (Text | CitationNode | MultipleCitationsNode)[] = [];
      let i = 0;
      while (i < newNodes.length) {
        const currentNode = newNodes[i];

        // Check if this is a citation node and start collecting consecutive citations
        if (currentNode.type === "citation") {
          const consecutiveCitations: CitationNode[] = [currentNode];
          let j = i + 1;

          // Collect consecutive citations, allowing whitespace text nodes in between
          while (j < newNodes.length) {
            const nextNode = newNodes[j];
            if (nextNode.type === "citation") {
              consecutiveCitations.push(nextNode);
              j++;
            } else if (
              nextNode.type === "text" &&
              nextNode.value.trim() === ""
            ) {
              // Skip whitespace between citations
              j++;
            } else {
              // Stop when we hit non-whitespace text
              break;
            }
          }

          // If we have 3 or more consecutive citations, group them
          if (consecutiveCitations.length >= 3) {
            const citationsData = consecutiveCitations.map((c) => ({
              citationId: c.data.hProperties.citationId,
              displayNumber: c.data.hProperties.displayNumber,
            }));

            finalNodes.push({
              type: "multipleCitations",
              data: {
                hName: "multiple-citations",
                hProperties: {
                  citations: JSON.stringify(citationsData),
                },
              },
              children: [],
            });
            i = j; // Skip all the citations we just grouped
          } else {
            // Less than 3 citations, add them individually
            finalNodes.push(currentNode);
            i++;
          }
        } else {
          // Not a citation, add as-is
          finalNodes.push(currentNode);
          i++;
        }
      }

      // Replace the original text node with the new nodes
      if (finalNodes.length > 0 && parent && typeof index === "number") {
        parent.children.splice(index, 1, ...(finalNodes as any));
        return index + finalNodes.length - 1; // Skip the newly added nodes
      }
    });
  };
};

interface ChatCitationNode {
  type: "chatCitation";
  data: {
    hName: string;
    hProperties: {
      displayLabel: string;
      displayNumber: number;
      markerKey?: string;
      displayText: string;
      // Exactly one of these open-target discriminators is set per node.
      docId?: string;
      publicId?: string;
      kind?: string;
      // The attribute an RMS citation names, when it names one (#11369).
      factName?: string;
      interactionId?: string;
      webUrl?: string;
      // Document evidence anchor (#10310).
      pageNum?: number;
      sourceContent?: string;
    };
  };
  children: never[];
}

// A collapsed run of 3+ consecutive chip citations. ``citations`` is the
// JSON-encoded list of the grouped chips' hProperties (each carries its own
// open-target + label + number), expanded by ChatMultipleCitationsButton.
interface ChatMultipleCitationsNode {
  type: "chatMultipleCitations";
  data: {
    hName: string;
    hProperties: {
      citations: string;
    };
  };
  children: never[];
}

// Matches a single legacy decimal marker `[NNNNN.M]` (4–7 digits, dot, 1–3
// digits). The model is prompted to cite individually, so we match each marker
// on its own; the captured group is the inner number (e.g. "10042.1"), while
// the sourceMap key is the full bracketed marker ("[10042.1]").
const CHAT_CITATION_REGEX = new RegExp(
  `\\[(${SIMPLE_CITATION_REGEX.source.slice(2, -2)})\\]`,
  "g"
);

// A RANGE citation marker `[NNNNN.a-b]` (e.g. `[37907.1-20]`) — the model
// compresses many consecutive IODL rows into one marker. The single-marker
// `CHAT_CITATION_REGEX` never matches it, so without expansion it neither
// renders as chips nor gets dropped and ships as literal text. The backend now
// expands these before persisting, so live answers arrive pre-expanded; this
// mirror keeps any range that reaches the renderer (a legacy answer persisted
// before the backend fix) from leaking — it is rewritten to individual markers,
// each of which then resolves against the sourceMap or is dropped like any
// unresolved marker. Group = 4–7 digits; a/b = 1–3 digit row components.
const CHAT_CITATION_RANGE_REGEX = /\[(\d{4,7})\.(\d{1,3})-(\d{1,3})\]/g;
// A single IODL result never stamps more rows than the page cap (250), so a
// wider span is malformed, not a real run of stamped rows.
const CHAT_CITATION_RANGE_MAX_SPAN = 250;

// Rewrite every `[NNNNN.a-b]` range in `text` to its constituent individual
// markers `[NNNNN.a][NNNNN.a+1]…[NNNNN.b]`. A descending or pathologically wide
// range is dropped rather than expanded (it is not a real run of stamped rows).
const expandCitationRanges = (text: string): string =>
  text.replace(
    CHAT_CITATION_RANGE_REGEX,
    (_full, group: string, a: string, b: string): string => {
      const start = parseInt(a, 10);
      const end = parseInt(b, 10);
      if (end < start || end - start >= CHAT_CITATION_RANGE_MAX_SPAN) {
        return "";
      }
      let out = "";
      for (let n = start; n <= end; n++) {
        out += `[${group}.${n}]`;
      }
      return out;
    }
  );

// Self-describing web citation marker `[[web:<url>]]`. The model copies the URL
// it read; the runtime never harvests it (URLs are self-resolving), so it's
// matched straight from the text and never looked up in the sourceMap. Web is
// the only `[[…]]` scheme remaining, so the inner capture is the bare URL.
const CHAT_WEB_CITATION_REGEX = /\[\[web:([^[\]]+)\]\]/g;

// Derive a short host label from a URL (e.g. "https://www.sec.gov/x" → "sec.gov"),
// stripping a leading `www.`. Falls back to the raw URL if it can't be parsed.
const webHostLabel = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

/**
 * Remark plugin for the agentic-chat legacy citation scheme. Turns `[NNNNN.M]`
 * markers into figures/chips resolved against the per-message legacy
 * `sourceMap` (keyed by the bracketed marker). A marker whose entry isn't in
 * the map is stripped (the model cited a source the runtime never harvested),
 * leaving no broken text behind.
 */
// Any mdast node that holds children, which is all the cross-node figure
// lookback needs to know about a node.
type MdParent = { children: unknown[] };

// One figure found in a node other than the marker's own, ready to be swapped
// for its citation link in place.
type ExternalFigure = {
  holder: MdParent;
  textNode: Text;
  fig: { start: number; end: number; value: string };
};

// Inline formatting the lookback descends through when hunting for a figure in
// a preceding sibling. Block nodes are absent on purpose: crossing a paragraph
// or a list item would bind a marker to a figure from a different statement.
//
// `link` is absent for a different reason — it is not formatting, it is a
// competing action. Binding inside one nests a citation control in an anchor:
// two click targets, two destinations, invalid interactive nesting.
//
// `inlineCode` is absent because it CANNOT work: mdast models it as a literal
// node carrying `value` with no `children`, so there is no text child to
// descend to. Listing it only looked like support.
const INLINE_FIGURE_WRAPPERS = new Set(["strong", "emphasis", "delete"]);

/**
 * Whether the text right after a figure labels it calculated — the
 * convention the backend verifier and delivery gate honour for a value no
 * source states (#12042). Only a label directly after the figure counts, so
 * `12.7% (Calculated as $127M …)` marks `12.7%` and not `$127M`. `after` is
 * rendered text (see `inlineText`), so emphasis around the label is already
 * gone.
 */
const labeledCalculated = (after: string): boolean =>
  /^\s*\(calculated\b/i.test(after);

/**
 * What may stand between a calculated figure and the calculation marker that
 * cites it (#13618): nothing but whitespace and an optional
 * `(calculated …)` label.
 */
const CALCULATION_TAIL = /^\s*(\(calculated\b[^)]*\)\s*)?$/i;

const isCalculation = (entry: ChatCitationSource): boolean =>
  chatSourceKind(entry) === "calculation";

/**
 * The rendered text of an inline mdast node: its text with markup removed. A
 * figure this pass already bound carries its text in `data`, not `value`, so
 * it reads as empty here; that can only make a label look adjacent, which
 * keeps a figure plain, never binds one.
 */
const inlineText = (n: unknown): string => {
  const value = (n as { value?: unknown }).value;
  if (typeof value === "string") return value;
  const kids = (n as MdParent).children;
  return Array.isArray(kids) ? kids.map(inlineText).join("") : "";
};

export const remarkChatCitations: Plugin<
  [
    {
      sourceMap?: Record<string, ChatCitationSource[]>;
      numbers?: Record<string, number>;
    }?,
  ],
  Root
> = (options = {}) => {
  const sourceMap = options.sourceMap || {};
  const numbers = options.numbers;
  let displayNumber = 1;
  // Numbers keyed by the source's identity, not the marker's: a document
  // cited at two passages is one source and carries one number, the way its
  // row in the answer's sources list does (#11426). A turn-wide numbering
  // handed in through `numbers` wins, so every text block of the turn agrees.
  const displayNumByKey: Record<string, number> = {};
  const markerToDisplayNum: Record<string, number> = {};
  const numberFor = (markerKey: string, sourceKey: string): number => {
    if (!markerToDisplayNum[markerKey]) {
      const given = numbers?.[markerKey];
      if (given !== undefined) {
        markerToDisplayNum[markerKey] = given;
      } else {
        if (!displayNumByKey[sourceKey])
          displayNumByKey[sourceKey] = displayNumber++;
        markerToDisplayNum[markerKey] = displayNumByKey[sourceKey];
      }
    }
    return markerToDisplayNum[markerKey];
  };
  return (tree) => {
    // remark-gfm autolinks the bare URL inside a `[[web:https://…]]` marker,
    // splitting it into text("…[[web:") + link + text("]]…"). Re-stitch those
    // back into one text node so the web-citation pass below sees a whole
    // marker. Web is the only `[[…]]` scheme, but `/\[\[[a-z]+:$/` still matches
    // `[[web:`.
    visit(tree, (node: any) => {
      const children = node.children;
      if (!Array.isArray(children)) return;
      for (let i = children.length - 3; i >= 0; i--) {
        const head = children[i];
        const link = children[i + 1];
        const tail = children[i + 2];
        if (
          head?.type === "text" &&
          /\[\[[a-z]+:$/.test(head.value) &&
          link?.type === "link" &&
          tail?.type === "text" &&
          tail.value.startsWith("]]")
        ) {
          head.value = head.value + String(link.url) + tail.value;
          children.splice(i + 1, 2);
        }
      }
    });

    // Expand any range marker `[NNNNN.a-b]` to individual markers first, so the
    // per-marker pass below renders each as a chip (or drops the unresolved
    // ones) instead of leaking the range as literal text. Live answers arrive
    // pre-expanded from the backend; this covers legacy persisted answers.
    visit(tree, "text", (node: Text) => {
      if (CHAT_CITATION_RANGE_REGEX.test(node.value)) {
        node.value = expandCitationRanges(node.value);
      }
      // A global regex keeps `lastIndex` between `.test()` calls; reset so the
      // next node starts clean.
      CHAT_CITATION_RANGE_REGEX.lastIndex = 0;
    });

    // Child → parent index, so the figure pass can look at a marker's sibling
    // NODES and (inside a table) at the other cells of its row (#11237). mdast
    // carries no parent pointers and `visit` only hands back the immediate
    // parent, so the map is built once per tree here.
    const parentOf = new Map<unknown, MdParent>();
    visit(tree, (n: unknown) => {
      const kids = (n as MdParent).children;
      if (Array.isArray(kids)) {
        for (const child of kids) {
          parentOf.set(child, n as MdParent);
        }
      }
    });

    // Numeric `[NNNNN.M]` markers (document chunks + RMS entities), resolved
    // against the per-message sourceMap.
    visit(tree, "text", (node: Text, index, parent) => {
      const regex = new RegExp(CHAT_CITATION_REGEX.source, "g");
      if (!regex.test(node.value)) {
        return;
      }
      regex.lastIndex = 0;

      const newNodes: (Text | ChatCitationNode)[] = [];

      // Pre-scan the node so a figure can consider every marker in the
      // consecutive run that follows its segment (e.g. "16.0% … [A][B]"), not
      // just the first one. Markers separated by only whitespace form a run.
      type ScannedMarker = {
        start: number;
        end: number;
        markerKey: string;
        entry: ChatCitationSource | undefined;
      };
      const scanned: ScannedMarker[] = [];
      let match: RegExpExecArray | null;
      while ((match = regex.exec(node.value)) !== null) {
        const markerKey = `[${match[1]}]`;
        scanned.push({
          start: match.index,
          end: match.index + match[0].length,
          markerKey,
          entry: sourceMap[markerKey]?.[0],
        });
      }
      const inSameRunAsPrevious = (i: number): boolean =>
        i > 0 &&
        /^\s*$/.test(node.value.slice(scanned[i - 1].end, scanned[i].start));
      // Markers a figure was bound to render no trailing chip (the blue
      // figure IS their affordance); every other marker keeps its chip.
      const figureBoundTo: boolean[] = scanned.map(() => false);

      const nodeForMarker = (
        markerKey: string,
        entry: ChatCitationSource,
        displayText: string
      ): ChatCitationNode => {
        // RMS record citations open the preview (publicId + kind); interaction
        // citations open the interaction viewer (interactionId); structured
        // data-source citations open the source viewer (the cited row, and the
        // SQL when the source exposes one); document citations open the
        // document (docId). Each labels to match.
        //
        // A structured citation that names an RMS record takes the preview
        // branch instead: a cited transaction line is money moving in or out of
        // one investment, and the reader wants that investment, not the query
        // behind it (#11103). Which is why the pair is tested here and not
        // `rmsEntity` — that flag marks how an entry was harvested, while the
        // pair is what the preview actually opens on.
        const sourceKind = chatSourceKind(entry);
        const displayLabel = chatSourceLabel(entry);
        numberFor(markerKey, chatSourceKey(entry, markerKey) ?? markerKey);
        return {
          type: "chatCitation",
          data: {
            hName: "chat-citation",
            hProperties: {
              ...(sourceKind === "calculation"
                ? {
                    // The card's arithmetic and each input resolved against
                    // this map, as one JSON string (arrays do not survive as
                    // element props), so every input row opens where its own
                    // chip would (#13618).
                    calculationPayload: JSON.stringify(
                      chatSourceCalculation(
                        entry,
                        sourceMap,
                        new Set([markerKey])
                      )
                    ),
                  }
                : sourceKind === "data"
                  ? {
                      // Arrays don't survive as element props, so the IODL payload
                      // rides as one JSON string (like chat-multiple-citations).
                      iodlPayload: JSON.stringify(chatSourceIodl(entry)),
                    }
                  : sourceKind === "record"
                    ? {
                        publicId: entry.publicId,
                        kind: entry.kind,
                        // The attribute the claim came from, when the citation
                        // names one: the preview highlights it and scrolls to it
                        // instead of opening on the record's first row (#11369).
                        factName: entry.factName,
                      }
                    : sourceKind === "interaction"
                      ? { interactionId: entry.interactionId }
                      : sourceKind === "artifact"
                        ? { artifactId: entry.artifactId }
                        : {
                            docId: entry.docId,
                            // Evidence anchor: page + verbatim chunk text ride to
                            // the click so the viewer lands on the passage (#10310).
                            pageNum: entry.pageNum ?? undefined,
                            sourceContent: entry.content || undefined,
                          }),
              markerKey,
              displayLabel,
              displayNumber: markerToDisplayNum[markerKey],
              // When set, this cited figure renders as blue clickable text
              // inline; otherwise the chip is a small trailing number.
              displayText,
            },
          },
          children: [],
        };
      };

      // A figure binds to the first marker in the run whose source actually
      // contains it (#10706 — binding blindly to the adjacent marker sent a
      // "16.0%" into a document whose only 16 is "USD 16 billion"). The
      // arbitration only runs when the run cites MORE THAN ONE document —
      // with a single document there is nothing to confuse (the backend's
      // single_source_answer rule, #10468), and chunk `content` is too lossy
      // to second-guess the binding (table chunks harvest caption text, so a
      // figure in the table body would fail a literal match and lose a link
      // into the right document). In multi-document runs, document chunks
      // validate against their text; RMS/interaction/IODL sources keep their
      // pre-validation binding (their "content" is not prose the figure can
      // be matched against), and so do document-level entries, whose content
      // is a filename stub (#10684 — computed figures over attached
      // spreadsheets cite the whole file). Spreadsheet chunks skip the
      // percent requirement, mirroring the backend's is_excel handling.
      //
      // `alwaysValidate` is for the second half of a range whose first half
      // the backend already linked (#12190). That half reaches this pass only
      // because the backend declined it, so the single-document shortcut does
      // not apply: it binds when a source's text holds it, and otherwise
      // stays uncited prose.
      const markerForFigure = (
        figValue: string,
        runStart: number,
        opts: { alwaysValidate?: boolean } = {}
      ): {
        index: number;
        markerKey: string;
        entry: ChatCitationSource;
      } | null => {
        const run: number[] = [];
        for (let j = runStart; j < scanned.length; j++) {
          if (j > runStart && !inSameRunAsPrevious(j)) {
            break;
          }
          run.push(j);
        }
        const sourceKeys = new Set<string>();
        for (const j of run) {
          const entry = scanned[j].entry;
          if (entry && !isCalculation(entry)) {
            sourceKeys.add(
              entry.docId ??
                entry.parentDocId ??
                entry.publicId ??
                entry.interactionId ??
                entry.sourceLabel ??
                scanned[j].markerKey
            );
          }
        }
        if (sourceKeys.size <= 1 && !opts.alwaysValidate) {
          const first = run.find((j) => {
            const entry = scanned[j].entry;
            return entry !== undefined && !isCalculation(entry);
          });
          if (first === undefined) {
            return null;
          }
          const entry = scanned[first].entry;
          return entry
            ? { index: first, markerKey: scanned[first].markerKey, entry }
            : null;
        }
        for (const j of run) {
          const entry = scanned[j].entry;
          if (!entry || isCalculation(entry)) {
            continue;
          }
          const validatable =
            entry.rmsEntity !== true &&
            entry.rmsInteraction !== true &&
            entry.iodlSource !== true &&
            entry.artifact !== true &&
            entry.docLevel !== true;
          const spreadsheet = /\.(xlsx|xls|csv)$/i.test(
            entry.parentDoc?.filename ?? ""
          );
          if (
            (!validatable && !opts.alwaysValidate) ||
            (validatable &&
              numericBindsToSource(figValue, entry.content, { spreadsheet }))
          ) {
            return { index: j, markerKey: scanned[j].markerKey, entry };
          }
        }
        return null;
      };

      // This node's live position among its siblings. `index` is captured
      // before any binding runs, and an external bind can splice into this
      // node's own parent — so everything that indexes into `parent.children`
      // after that point reads this instead (#11237).
      let nodeIndex = typeof index === "number" ? index : -1;

      // Figures that live in a SIBLING node rather than in this text node
      // (#11237). Markdown routinely separates a figure from its marker:
      // `**16.0%** [m]` puts the figure in a `strong`, and a table puts it in
      // an earlier cell. Neither could ever blue-link, because the pass below
      // only sees text preceding the marker inside this one node.
      //
      // Bounded to this node's own PARENT — preceding siblings, descending
      // only through inline formatting — and, inside a table, earlier cells of
      // the SAME row. It never crosses a paragraph, list item or table row,
      // because that is where a marker starts attaching to a value from a
      // different record. Within the parent it does scan back past earlier
      // sentences, which matches what the in-node pass already does.
      const externalFigures = (): ExternalFigure[] => {
        if (index === undefined || parent === undefined) {
          return [];
        }
        const found: ExternalFigure[] = [];
        const collect = (n: unknown): void => {
          const kind = (n as { type?: string }).type;
          if (kind === "text") {
            const textNode = n as Text;
            const holder = parentOf.get(textNode);
            if (!holder) return;
            const figs = findCitableNumerics(textNode.value);
            // Nearest-first: the figure closest to the marker is the one it
            // most plausibly cites.
            for (let f = figs.length - 1; f >= 0; f--) {
              found.push({ holder, textNode, fig: figs[f] });
            }
            return;
          }
          if (!kind || !INLINE_FIGURE_WRAPPERS.has(kind)) return;
          const kids = (n as MdParent).children;
          if (!Array.isArray(kids)) return;
          for (let k = kids.length - 1; k >= 0; k--) collect(kids[k]);
        };

        const siblings = (parent as MdParent).children;
        for (let s = nodeIndex - 1; s >= 0; s--) collect(siblings[s]);

        // A marker in one table cell binds a figure from an earlier cell of
        // its own row — never another row, which would be a different record.
        if ((parent as { type?: string }).type === "tableCell") {
          const row = parentOf.get(parent);
          if (row && (row as { type?: string }).type === "tableRow") {
            const cells = row.children;
            for (let c = cells.indexOf(parent) - 1; c >= 0; c--) {
              const cellKids = (cells[c] as MdParent).children;
              if (!Array.isArray(cellKids)) continue;
              for (let k = cellKids.length - 1; k >= 0; k--)
                collect(cellKids[k]);
            }
          }
        }
        return found;
      };

      // The rendered text between a cross-node figure and this node's marker,
      // in document order: the rest of the figure's own text, every inline
      // node after it, the cells between it and this one, and this node's
      // text up to the marker (`before`). One answer to "what follows this
      // figure", so markup between the figure and its label — an italic
      // `*(calculated …)*`, a bold word inside the label — cannot hide the
      // label from the check (#12042 review).
      const textAfterFigure = (
        textNode: Text,
        figEnd: number,
        before: string
      ): string => {
        let out = textNode.value.slice(figEnd);
        let cur: unknown = textNode;
        for (;;) {
          const holder = parentOf.get(cur);
          if (!holder) return out;
          const kids: unknown[] = holder.children;
          for (let k = kids.indexOf(cur) + 1; k < kids.length; k++) {
            const kid = kids[k];
            if (kid === node) return out + before;
            if (parent !== undefined && kid === parent) {
              const lead = (parent as MdParent).children.slice(0, nodeIndex);
              return out + lead.map(inlineText).join("") + before;
            }
            out += inlineText(kid);
          }
          cur = holder;
        }
      };

      // Swap a figure inside a sibling node for its blue citation link, in
      // place.
      //
      // The holder is frequently THIS node's own parent, not some deeper node:
      // any inline node between the figure and the marker splits the paragraph
      // into several text children, so `2.0% *(net)* [marker]` puts the figure
      // in a plain text sibling. Splicing there shifts this node's position, so
      // `nodeIndex` is re-derived afterwards — the replacement below indexes
      // into the same array and would otherwise delete the wrong child, drop
      // prose and duplicate the rest.
      const bindExternalFigure = (
        candidate: ExternalFigure,
        target: { markerKey: string; entry: ChatCitationSource }
      ): boolean => {
        const kids = candidate.holder.children;
        const at = kids.indexOf(candidate.textNode);
        if (at === -1) return false;
        const value = candidate.textNode.value;
        const replacement: (Text | ChatCitationNode)[] = [];
        if (candidate.fig.start > 0) {
          replacement.push({
            type: "text",
            value: value.slice(0, candidate.fig.start),
          });
        }
        replacement.push(
          nodeForMarker(target.markerKey, target.entry, candidate.fig.value)
        );
        if (candidate.fig.end < value.length) {
          replacement.push({
            type: "text",
            value: value.slice(candidate.fig.end),
          });
        }
        kids.splice(at, 1, ...replacement);
        if (parent) {
          nodeIndex = (parent as MdParent).children.indexOf(node);
        }
        return true;
      };

      // Whether this marker's source is ALREADY a blue figure in this node's
      // own cell (or paragraph), because the backend linked it.
      //
      // `table_links.py` links figures the legacy injector declined and, by
      // contract, "adds links and nothing else: no marker is consumed" — the
      // marker may be the only citation covering other figures in the row, so
      // consuming it there could orphan them. The rule below already says a
      // resolved marker drops its chip when a figure bound to it, but that
      // binding only sees `text` nodes: a figure arriving as a `link` node was
      // invisible to it, so a cell holding both drew a blue figure AND a
      // numbered bubble for one source (#11454).
      //
      // Matched on citation identity, never on document: a marker citing a
      // DIFFERENT source than the link beside it is a second citation, and
      // dropping its chip would hide a citation rather than de-duplicate one.
      const markerAlreadyLinked = (markerKey: string): boolean => {
        if (parent === undefined) return false;
        const cid = markerKey.slice(1, -1);
        if (!cid) return false;

        let linked = false;
        const scan = (n: unknown): void => {
          if (linked) return;
          const kind = (n as { type?: string }).type;
          if (kind === "link") {
            const query = ((n as { url?: string }).url ?? "").split("?")[1];
            if (query && query.split("&").some((p) => p === `cid=${cid}`)) {
              linked = true;
            }
            return;
          }
          const kids = (n as MdParent).children;
          if (Array.isArray(kids)) for (const k of kids) scan(k);
        };

        // This node's own parent only. Scoping wider would let one cell's link
        // silence a neighbouring cell's citation, and every cell of the row
        // this was found in carries the same cid.
        const siblings = (parent as MdParent).children;
        for (let s = 0; s < siblings.length && !linked; s++) {
          if (s !== nodeIndex) scan(siblings[s]);
        }
        return linked;
      };

      // Whether this text node directly follows a backend citation link. A
      // dash opening the node is then the separator of a range whose first
      // number is that link, never a minus sign (#12190). Read before any
      // binding runs, since an external bind can splice into the parent.
      const previousSibling =
        parent !== undefined && nodeIndex > 0
          ? ((parent as MdParent).children[nodeIndex - 1] as {
              type?: string;
              url?: string;
            })
          : undefined;
      const followsCitationLink =
        previousSibling?.type === "link" &&
        /[?&]cid=/.test(previousSibling.url ?? "");

      let lastIndex = 0;
      for (let i = 0; i < scanned.length; i++) {
        const marker = scanned[i];
        const before = node.value.slice(lastIndex, marker.start);

        // Highlight every citable figure in the segment this citation covers
        // (e.g. "~24%" and ">13%"), matching the legacy line-association — not
        // just the figure adjacent to the marker. A figure no source in the
        // run validates stays plain prose: no link beats a wrong link. An
        // unresolved marker (the model cited something the runtime never
        // harvested) is transparent here: its segment's figures may still
        // bind to a resolved marker later in the same run.
        // A figure the answer labels `(calculated …)` is one no source states
        // (#12042): the marker after the label cites its INPUTS. Binding the
        // figure to it drew a calculated value as a quoted one, and its click
        // searched the sheet for a value the sheet never holds. It stays prose
        // and the marker keeps its chip; inputs written inside the label
        // (`(Calculated as $127M / …)`) still bind.
        const segmentFigures = findCitableNumerics(before, {
          followsCitationLink: lastIndex === 0 && followsCitationLink,
        });
        // A recorded calculation (#13618) is cited right after the figure it
        // produced. That figure, and only that one, opens its card: the last
        // figure of the segment, with nothing after it but an optional
        // `(calculated …)` label, which the chip and the card now say, so it
        // is not rendered. It is never a link into a document (#13445).
        const calculationFigure =
          marker.entry && isCalculation(marker.entry)
            ? segmentFigures
                .filter((fig) => CALCULATION_TAIL.test(before.slice(fig.end)))
                .pop()
            : undefined;
        const figures = segmentFigures.filter(
          (fig) =>
            fig !== calculationFigure &&
            !labeledCalculated(before.slice(fig.end))
        );
        // The marker after a calculated figure's label was written for that
        // figure's inputs; no other figure takes it (delta review, #12042).
        const droppedCalculated = figures.length < segmentFigures.length;
        // A dash range binds as one figure when one source holds both ends.
        // With one end found, that end alone binds; the dash and the other
        // end stay prose (#12190). `numericBindsToSource` requires every
        // number in the token, so the whole-range attempt IS the both-ends
        // check wherever this pass validates at all.
        type Bound = {
          start: number;
          end: number;
          value: string;
          target: NonNullable<ReturnType<typeof markerForFigure>>;
        };
        const bound: Bound[] = [];
        for (const fig of figures) {
          const whole = markerForFigure(fig.value, i, {
            alwaysValidate: fig.rangeOrphan === true,
          });
          if (whole !== null) {
            bound.push({ ...fig, target: whole });
            continue;
          }
          for (const rangeEnd of fig.range
            ? [fig.range.left, fig.range.right]
            : []) {
            for (const candidate of rangeEnd.candidates) {
              const target = markerForFigure(candidate, i);
              if (target !== null) {
                bound.push({
                  start: rangeEnd.start,
                  end: rangeEnd.end,
                  value: rangeEnd.value,
                  target,
                });
                break;
              }
            }
          }
        }
        if (calculationFigure && marker.entry) {
          bound.push({
            ...calculationFigure,
            // Through the end of the label, so its text is not rendered;
            // one space stays between the figure and the chip.
            end: before.length,
            target: {
              index: i,
              markerKey: marker.markerKey,
              entry: marker.entry,
            },
          });
          bound.sort((a, b) => a.start - b.start);
        }
        let cursor = 0;
        for (const { target, ...fig } of bound) {
          if (fig.start > cursor) {
            newNodes.push({
              type: "text",
              value: before.slice(cursor, fig.start),
            });
          }
          newNodes.push(
            nodeForMarker(target.markerKey, target.entry, fig.value)
          );
          if (isCalculation(target.entry)) {
            // The chip stays beside its figure: it is what says the figure
            // was calculated.
            newNodes.push({ type: "text", value: " " });
          } else {
            figureBoundTo[target.index] = true;
          }
          cursor = fig.end;
        }
        if (cursor < before.length) {
          newNodes.push({ type: "text", value: before.slice(cursor) });
        }
        // A calculation's figure in the node before this one (`**63.3**
        // [calc]`, an earlier cell's value) opens its card, as a bolded
        // document-stated figure opens its document: only the nearest
        // figure, with nothing between it and the marker but whitespace and
        // an optional `(calculated …)` label (#13618). The chip stays, and a
        // label written in this node is not rendered.
        const calculationEntry =
          marker.entry && isCalculation(marker.entry)
            ? marker.entry
            : undefined;
        if (
          i === 0 &&
          calculationEntry &&
          !figures.length &&
          !calculationFigure &&
          !figureBoundTo.some(Boolean)
        ) {
          const nearest = externalFigures()[0];
          if (
            nearest &&
            CALCULATION_TAIL.test(
              textAfterFigure(nearest.textNode, nearest.fig.end, before)
            ) &&
            bindExternalFigure(nearest, {
              markerKey: marker.markerKey,
              entry: calculationEntry,
            })
          ) {
            const last = newNodes[newNodes.length - 1];
            if (
              last?.type === "text" &&
              last.value === before &&
              CALCULATION_TAIL.test(before)
            ) {
              last.value = " ";
            }
          }
        }
        // Nothing in this node bound to the run starting at `i`? The figure
        // may be in the node before it — a bolded figure, or an earlier table
        // cell (#11237). Only through the SAME `markerForFigure` arbitration,
        // so widening where a figure may come from does not widen what it may
        // bind to.
        //
        // `i === 0` is the load-bearing restriction, not a micro-optimization.
        // An external figure sits immediately BEFORE this text node, so the
        // only run adjacent to it is the node's first. Without that clause a
        // figure whose own run refused it (the #10706 digit-coincidence guard)
        // got a second, unearned attempt at every later run in the node — and
        // a later run citing a single document short-circuits arbitration
        // entirely, so the refused figure would blue-link into a document
        // across a sentence boundary with no validation at all. That is the
        // exact wrong-value binding this issue exists to remove.
        if (
          !calculationEntry &&
          i === 0 &&
          !figures.length &&
          !calculationFigure &&
          !droppedCalculated &&
          !figureBoundTo.some(Boolean)
        ) {
          for (const candidate of externalFigures()) {
            // `**5.29%** *(calculated …)* [m]`: a calculated figure is not
            // bound across nodes either (#12042). The search ends there, not
            // skips it: the next candidate is an earlier figure the label's
            // marker was never written for, so the marker keeps its chip.
            if (
              labeledCalculated(
                textAfterFigure(candidate.textNode, candidate.fig.end, before)
              )
            ) {
              break;
            }
            const target = markerForFigure(candidate.fig.value, i);
            if (target === null) {
              continue;
            }
            if (bindExternalFigure(candidate, target)) {
              figureBoundTo[target.index] = true;
              break;
            }
          }
        }
        // Unresolved markers are dropped without a chip — never leak the raw
        // `[...]`. Resolved markers keep their chip unless a figure bound to
        // them, or the backend already linked one (the blue figure IS their
        // affordance either way).
        if (
          marker.entry &&
          !figureBoundTo[i] &&
          !markerAlreadyLinked(marker.markerKey)
        ) {
          newNodes.push(nodeForMarker(marker.markerKey, marker.entry, ""));
        } else {
          // No chip replaces this marker, so collapse the whitespace it sat
          // in: "expected [m]." otherwise ships "expected ." and
          // "return [m] is" ships "return  is" (#11454 live testing). Only
          // when what follows is whitespace or closing punctuation — the
          // following text keeps its own separator, so trimming here never
          // joins two words.
          const following = node.value.slice(marker.end);
          const last = newNodes[newNodes.length - 1];
          if (
            last &&
            last.type === "text" &&
            /[ \t]$/.test(last.value) &&
            /^[\s.,;:!?)\]]/.test(following)
          ) {
            // Spaces and tabs only — a trailing newline is a markdown soft
            // break and must survive.
            last.value = last.value.replace(/[ \t]+$/, "");
            if (!last.value) {
              newNodes.pop();
            }
          }
        }

        lastIndex = marker.end;
      }

      if (lastIndex < node.value.length) {
        const rest = node.value.slice(lastIndex);
        if (rest) {
          newNodes.push({ type: "text", value: rest });
        }
      }

      // Collapse a run of 3+ consecutive chip citations (no inline figure,
      // only whitespace between them) into one "N citations" dropdown —
      // mirrors the legacy MultipleCitations behavior. Blue-figure citations
      // (displayText set) are inline targets, not chips, so they don't group.
      const isChip = (n: Text | ChatCitationNode): boolean =>
        n.type === "chatCitation" && !n.data.hProperties.displayText;
      const groupedNodes: (
        | Text
        | ChatCitationNode
        | ChatMultipleCitationsNode
      )[] = [];
      let gi = 0;
      while (gi < newNodes.length) {
        const cur = newNodes[gi];
        if (isChip(cur)) {
          const run: ChatCitationNode[] = [cur as ChatCitationNode];
          let gj = gi + 1;
          while (gj < newNodes.length) {
            const nxt = newNodes[gj];
            if (isChip(nxt)) {
              run.push(nxt as ChatCitationNode);
              gj++;
            } else if (nxt.type === "text" && nxt.value.trim() === "") {
              gj++; // whitespace between chips is absorbed into the group
            } else {
              break;
            }
          }
          if (run.length >= 3) {
            groupedNodes.push({
              type: "chatMultipleCitations",
              data: {
                hName: "chat-multiple-citations",
                hProperties: {
                  citations: JSON.stringify(run.map((c) => c.data.hProperties)),
                },
              },
              children: [],
            });
            gi = gj;
          } else {
            groupedNodes.push(cur);
            gi++;
          }
        } else {
          groupedNodes.push(cur);
          gi++;
        }
      }

      if (parent && nodeIndex >= 0) {
        parent.children.splice(nodeIndex, 1, ...(groupedNodes as any));
        return nodeIndex + groupedNodes.length - 1;
      }
    });

    // Self-describing web markers `[[web:<url>]]`. Run as a separate pass after
    // the re-stitch above so each marker is whole. Web markers are not looked up
    // in the sourceMap — the URL is the open target, labeled by its host.
    visit(tree, "text", (node: Text, index, parent) => {
      const regex = new RegExp(CHAT_WEB_CITATION_REGEX.source, "g");
      if (!regex.test(node.value)) {
        return;
      }
      regex.lastIndex = 0;

      const newNodes: (Text | ChatCitationNode)[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = regex.exec(node.value)) !== null) {
        const [fullMatch, url] = match;
        const matchStart = match.index;
        const markerKey = `[[web:${url}]]`;

        const before = node.value.slice(lastIndex, matchStart);
        if (before) {
          newNodes.push({ type: "text", value: before });
        }
        numberFor(markerKey, markerKey);

        newNodes.push({
          type: "chatCitation",
          data: {
            hName: "chat-citation",
            hProperties: {
              webUrl: url,
              displayLabel: webHostLabel(url),
              displayNumber: markerToDisplayNum[markerKey],
              displayText: "",
            },
          },
          children: [],
        });

        lastIndex = matchStart + fullMatch.length;
      }

      if (lastIndex < node.value.length) {
        const rest = node.value.slice(lastIndex);
        if (rest) {
          newNodes.push({ type: "text", value: rest });
        }
      }

      if (parent && typeof index === "number") {
        parent.children.splice(index, 1, ...(newNodes as any));
        return index + newNodes.length - 1;
      }
    });
  };
};
